import { useMutation } from 'react-query'
import { sendToApi } from 'api/client'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { PreviewTopicsListResponse, PreviewTopicsResponse } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useRegenerateBrandPlanScheduleTopics } from 'hooks/queries/brandPlans/useRegenerateBrandPlanScheduleTopics'
import { UseMutationOptions } from 'react-query/types/react/types'

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 30_000

interface PreviewTopicsSyncVariables {
  scheduleId: number
  contextSourceIds?: number[]
}

export interface Options
  extends Omit<UseMutationOptions<PreviewTopicsResponse, Error, PreviewTopicsSyncVariables>, 'mutationFn'> {}

const fetchPreviewTopics = async (workspaceId: number, scheduleId: number): Promise<PreviewTopicsListResponse> => {
  const res = await sendToApi<PreviewTopicsListResponse>(
    `/w/${workspaceId}/brand_plan_schedules/${scheduleId}/preview_topics`,
  )
  if (res.type !== SUCCESS_RESPONSE_TYPE) throw new Error(res.data)
  return res.data
}

export const usePreviewTopicsSync = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: regenerateTopics } = useRegenerateBrandPlanScheduleTopics()

  return useMutation<PreviewTopicsResponse, Error, PreviewTopicsSyncVariables>(async ({ scheduleId, contextSourceIds }) => {
    const sections: ('social' | 'video' | 'seo' | 'email')[] = ['social', 'video', 'seo', 'email']

    const tryRegenerate = async () => {
      try {
        await regenerateTopics({
          scheduleId,
          sections,
          preview: true,
          regenerateContentAfter: false,
          contextSourceIds,
        })
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error)
        if (!msg.includes('Already regenerating')) throw error
      }
    }

    await tryRegenerate()

    const deadline = Date.now() + TIMEOUT_MS
    let retriedAfterEmpty = false

    let data: PreviewTopicsListResponse

    do {
      data = await fetchPreviewTopics(workspaceId, scheduleId)

      const errorPreview = data.previews.find((p) => p.error)
      if (errorPreview) throw new Error(errorPreview.error || 'Topic generation failed')

      const readyPreview = data.previews.find((p) => p.status === 'ready')
      if (readyPreview) return readyPreview

      if (data.previews.length === 0 && !retriedAfterEmpty) {
        retriedAfterEmpty = true
        await tryRegenerate()
      }

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
    } while (Date.now() < deadline)

    throw new Error('Topic generation timed out')
  }, options)
}
